import React, { useMemo, useState } from 'react';
import './SkillCard.less';

export const formatNumber = (num: number) => {
  if (!num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
  return num.toString();
};

export const getPluginDescription = (skill: any): string => {
  try {
    if (skill.pluginJson) {
      const pj = typeof skill.pluginJson === 'string' ? JSON.parse(skill.pluginJson) : skill.pluginJson;
      const nls = pj.nls || {};
      return nls['zh-cn']?.['plugin.description']
        || nls['default']?.['plugin.description']
        || nls['en-us']?.['plugin.description']
        || pj.description
        || '';
    }
  } catch (e) {}
  return skill.skillDescription || skill.info || skill.description || '暂无描述';
};

interface SkillCardProps {
  skill: any;
  index: number;
}

const PROVIDE_SECTIONS = [
  { key: 'workflows', title: 'WORKFLOWS' },
  { key: 'agents', title: 'AGENTS' },
  { key: 'skills', title: 'SKILLS' },
  { key: 'hooks', title: 'HOOKS' }
];

const parseTruncatedYnfVersionInfo = (value: string) => {
  const result: any = {
    workflows: [],
    agents: [],
    skills: [],
    hooks: []
  };
  const pathRegExp = /"path"\s*:\s*"([^"]+)"/g;
  let match = pathRegExp.exec(value);

  while (match) {
    const path = match[1];
    const section = PROVIDE_SECTIONS.find(({ key }) => path.indexOf(`${key}/`) === 0);
    if (section && !result[section.key].some((item: any) => item.path === path)) {
      result[section.key].push({ path, name: path.split('/').pop() });
    }
    match = pathRegExp.exec(value);
  }

  return result;
};

const parseYnfVersionInfo = (value: any) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch (error) {
      return parseTruncatedYnfVersionInfo(value);
    }
  }
  return value;
};

const getProvideSource = (ynfVersionInfo: any) => {
  const parsed = parseYnfVersionInfo(ynfVersionInfo);
  return parsed.provides || parsed;
};

const getProvidePath = (item: any) => {
  if (typeof item === 'string') return item;
  return item?.path || item?.name || '';
};

const SkillCard: React.FC<SkillCardProps> = ({ skill, index }) => {
  const [expanded, setExpanded] = useState(false);

  const provideSource = useMemo(() => getProvideSource(skill.ynfVersionInfo), [skill.ynfVersionInfo]);
  const hasProvidedContent = PROVIDE_SECTIONS.some(({ key }) => Array.isArray(provideSource[key]) && provideSource[key].length > 0);

  const handleClick = () => {
    window.location.href = `/plugin-market/detail/${skill.name}`;
  };

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setExpanded(!expanded);
  };

  const renderProvidedSection = (title: string, items: any[]) => {
    if (!Array.isArray(items) || items.length === 0) return null;

    return (
      <div className="provide-section" key={title}>
        <div className="provide-title">{title}</div>
        <div className="provide-paths">
          {items.map((item, itemIndex) => {
            const providePath = getProvidePath(item);
            if (!providePath) return null;

            return (
              <div className="provide-path" key={`${title}-${providePath}-${itemIndex}`} title={providePath}>
                {providePath}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div
      className="skill-card"
      style={{ animationDelay: `${index * 0.05}s` }}
      onClick={handleClick}
    >
      <div className="skill-card-header">
        <h3 className="skill-name" title={skill.name}>
          {skill.name || '未命名插件'}
        </h3>
        {skill.version && (
          <span className="skill-version">v{skill.version}</span>
        )}
        <button className="skill-expand-btn" onClick={handleToggle}>
          {expanded ? '收起' : '展开'}
        </button>
      </div>

      <p className="skill-description">
        {getPluginDescription(skill)}
      </p>

      <div className="skill-stats">
        <span className="stat-item">
          <span className="stat-icon">📥</span>
          <span className="stat-value">{formatNumber(skill.download || 0)}</span>
        </span>
      </div>

      {expanded && (
        <div className="skill-provides" onClick={(event) => event.stopPropagation()}>
          <div className="provides-heading">提供的内容</div>
          {hasProvidedContent ? (
            PROVIDE_SECTIONS.map(({ key, title }) => renderProvidedSection(title, provideSource[key]))
          ) : (
            <div className="provide-empty">暂无提供的内容</div>
          )}
        </div>
      )}

      <div className="skill-author">
        <span className="author-text">by @{skill.creatorName || skill.author || 'anonymous'}</span>
        <span className="author-divider">·</span>
        <span className="update-time">更新时间 {skill.latestTime || '--'}</span>
      </div>
    </div>
  );
};

export default SkillCard;
